<script lang="ts">
  import MixBar from './MixBar.svelte';
  import InfoTip from './InfoTip.svelte';
  import { TERMINAL_ROLE } from '@ui/quadrant';
  import { navigate } from '../router.svelte';
  import { activeCommunity } from '../community.svelte';
  import { parseTs, timeAgo } from '../admin';
  import type { RevenueBlock } from '@api';

  /**
   * Three figures: what the membership pays now, what of that is wobbling, and what
   * already left. Mounted on the Overview and on Pipelines, from the SAME `revenue`
   * block of the one insights request each page already makes.
   *
   * BUILT ON `<StatCard>`, not on its own boxes. The first version hand-rolled three
   * bordered divs with their own type scale, which is how a dashboard ends up with two
   * kinds of KPI card that differ by a few pixels — the same drift `<PageHeader>` and
   * `<PageContainer>` exist to stop, one level down. StatCard already owns the label,
   * the tabular figure, the sub-line, the info tip and the action slot.
   *
   * WHY THREE AND NOT ONE NETTED NUMBER. `at_risk` is a subset of `live`; `churned`
   * already left `live`. So `live − at_risk` is a worst case for next month and
   * `churned` is history over a 30-day window. Netting them would double-count money
   * that is already gone.
   *
   * EVERY TILE LEADS TO THE MEMBERSHIP BOARD, including the locked state. A number
   * without a next step is a number you read and forget: the board is where these
   * members actually get worked, and it is filtered to exactly the people behind the
   * figure above it.
   *
   * THE LOCKED CASE IS THE MAJORITY CASE. Plan prices come only from Skool's membership
   * tabs, which need a staff role: of 67 communities we ingest, five can produce these
   * numbers. So "we can't see this yet" renders as the ask — reading `roster_state`, the
   * same signal `<RosterAccessBanner>` uses — instead of three zeros that would read as
   * "your community earns nothing".
   */
  let {
    revenue = null,
    updated = null,
    dense = false,
    onOpenSegment = null,
  }: {
    revenue?: RevenueBlock | null;
    /** One slim strip instead of three tiles — for a page where these numbers are
     *  CONTEXT rather than the subject.
     *
     *  `dense`, not `compact`: there is already a `compact()` in this file that
     *  abbreviates money, and a prop shadowing it would read as the same idea.
     *
     *  The Pipelines board is the case. Three tiles carrying nine figures, three mix
     *  bars and three legends push the board itself below the fold on a laptop, and the
     *  board is what that page is FOR. The Overview keeps the full version, where the
     *  numbers are the subject and the bars are the answer to "made up of what".
     *
     *  What survives the cut is the CLICK: every figure still opens its own segment, so
     *  this is a change of density, not of capability. */
    dense?: boolean;
    /** The roster sweep this was read from, NOT the page's window: revenue is a reading
     *  of the roster as it stands and has no "last 90 days" form. */
    updated?: string | null | undefined;
    /** Supplied by the Pipelines page, which is already showing the boards. There a
     *  click must switch board AND apply the segment IN PLACE: navigating to
     *  `/admin/pipelines?...` from `/admin/pipelines` changes the URL without remounting
     *  anything, and the board reads `?segment=` only on a cold load — so the figure
     *  looked inert. Takes the segment, so every figure and slice lands on its own
     *  members rather than on whichever board the page happened to be showing. */
    onOpenSegment?: ((segment: string, board: string) => void) | null;
  } = $props();

  /**
   * WHICH BOARD EACH FIGURE OPENS.
   *
   * The segment names the GROUP; the board has to name what you can do about them. So
   * the split is by the owner's next action, not by where the number was displayed:
   *
   *   money questions      -> Membership   (Unpaid · Cancelled · Paying · Churned)
   *   behaviour questions  -> Engagement   (Dropped out · Slipping · Contributing)
   *
   * Cancelled money lands on the board with a Cancelled column, where the action is a
   * save offer. Churned lands beside Churned. Plan mix and lifetime spend are plan
   * questions and only Membership has plan columns.
   *
   * `risk_to_cancel` is the one that could plausibly go either way, and it goes to
   * ENGAGEMENT: nobody in it has cancelled, so there is no billing action to take — the
   * only thing that changes their mind is participation. Membership would file them
   * under "Paying" beside members who are perfectly happy, which is precisely the
   * distinction the figure exists to draw. Not Onboarding either: that board is the
   * first-contribution journey for NEW members, and a payer who has been quiet for a
   * year is not an onboarding case. Engagement carries "Not onboarded yet" for the ones
   * who never started, so it covers the whole quiet population on one board.
   *
   * `dropping_off` is literally Engagement's "Slipping", and `engaged` its
   * "Contributing" — those two need no argument.
   */
  const SEGMENT_BOARD: Record<string, string> = {
    monthly: 'paying_membership',
    annual: 'paying_membership',
    unpriced: 'paying_membership',
    paying: 'paying_membership',
    collected: 'paying_membership',
    cancelled: 'paying_membership',
    churned: 'paying_membership',
    risk_to_cancel: 'engagement',
    dropping_off: 'engagement',
    engaged: 'engagement',
  };

  const MEMBERSHIP_BOARD = '/admin/pipelines?pipeline=paying_membership';

  /** Where a figure goes when clicked: its board, filtered to its own members. */
  const segmentHref = (seg: string): string =>
    `/admin/pipelines?pipeline=${SEGMENT_BOARD[seg] ?? 'paying_membership'}&segment=${seg}`;
  // "See who left" already means one thing in this app — `<ContributorRoles>` sends it
  // to the members table filtered to the terminal role. Same words, same destination:
  // two links with one label going to two places is worse than either choice.
  const DEPARTED_MEMBERS = `/admin/community/members?quadrant=${TERMINAL_ROLE}`;

  const active = $derived(activeCommunity());
  // `ask` = no staff role, the owner can fix it. `escalate` = we hold a role and Skool
  // still won't show the tabs, which the owner CANNOT fix by adding us again — two
  // different sentences, already distinguished server-side (`api/plans.py`).
  const rosterState = $derived(active?.roster_state ?? 'unknown');
  const blocked = $derived(rosterState === 'ask' || rosterState === 'escalate');

  const r = $derived(revenue);
  const noPrices = $derived(!!r && r.paying > 0 && r.priced === 0);

  // The theme's danger red, matching `at_risk` on the momentum axis (@ui/stages) so
  // "this member is slipping" and "this money is slipping" read as the same warning.
  const DANGER = '#bd3a30';
  // Money coming IN is green — the same deep green `rising` carries on the momentum
  // axis (@ui/stages). These three figures are the only unambiguously good numbers
  // on the page and they were rendering in the same near-black as a post count.
  const POSITIVE = '#157a49';

  let nowMs = $state(Date.now());
  $effect(() => {
    const id = setInterval(() => (nowMs = Date.now()), 30_000);
    return () => clearInterval(id);
  });

  /** Open a segment's members: in place when the host page is already the board, by
   *  navigation otherwise. One function so a figure, a bar slice and a legend entry
   *  cannot behave differently. */
  function openSegment(seg: string): void {
    const board = SEGMENT_BOARD[seg] ?? 'paying_membership';
    if (onOpenSegment) onOpenSegment(seg, board);
    else navigate(segmentHref(seg));
  }

  /** The locked panel's link has no segment — there are no figures to drill into yet. */
  function openBoard(): void {
    if (onOpenSegment) onOpenSegment('', 'paying_membership');
    else navigate(MEMBERSHIP_BOARD);
  }

  /** Whole dollars — cents are noise on a run-rate figure, and the number is a floor
   *  anyway whenever we hold a price for fewer members than are paying. */
  const money = (cents: number): string => `$${Math.round(Math.abs(cents) / 100).toLocaleString()}`;

  /** The same figure, shortened once it stops fitting. Three metrics share a tile, so a
   *  tile is ~344px wide on a 1440px laptop and each column gets ~114px; "$93,717" in the
   *  display face is wider than that and wrapped the row onto two lines. Abbreviating at
   *  10k keeps every column one line at every width, and the exact figure stays one hover
   *  away rather than being lost. Below 10k nothing is abbreviated: "$7,362" is both
   *  precise and short, and "$7.4k" would throw away real precision for no space. */
  function compact(cents: number): string {
    const v = Math.abs(cents) / 100;
    if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
    if (v >= 10_000) return `$${(v / 1000).toFixed(1).replace(/\.0$/, '')}k`;
    return `$${Math.round(v).toLocaleString()}`;
  }
  const signedCompact = (cents: number): string => (cents > 0 ? `−${compact(cents)}` : compact(cents));
  const signed = (cents: number): string => (cents > 0 ? `−${money(cents)}` : money(cents));
  const plural = (n: number, one: string, many: string): string => (n === 1 ? one : many);
</script>

<!-- ONE metric, used by all three tiles. They were hand-rolling the same twelve lines
     each, which is how three figures end up at three sizes. `title` carries the exact
     amount so `compact()` never destroys information — it only defers it.

     A GRID, not a flex row with dividers. The row used `gap-x-4` plus two 1px rules and
     relied on the numbers being short; at 1440px the tile is ~344px and the three
     figures needed ~422px, so it silently wrapped onto two lines and the tile grew. Three
     equal columns can't collide at any width, and dropping the rules is what makes it
     read as one object rather than three things pushed together. -->
{#snippet metric(value: string, label: string, exact: string, color: string | null,
                 segment: string | null = null)}
  <!-- A figure is a QUESTION, and the board is where it gets answered — so every one of
       them opens its own members rather than being read and forgotten. `title` carries
       the exact amount (which `compact()` may have shortened) and the destination, so
       hovering explains both what the number is and where it goes. -->
  <svelte:element
    this={segment ? 'button' : 'div'}
    role={segment ? 'link' : undefined}
    type={segment ? 'button' : undefined}
    onclick={segment ? () => openSegment(segment) : undefined}
    class="block min-w-0 text-left {segment ? 'group/metric cursor-pointer' : ''}"
    title={segment ? `${exact} — open the ${SEGMENT_BOARD[segment] === 'engagement' ? 'engagement' : 'membership'} board` : exact}
  >
    <!-- The figure is redacted, the label under it is not — every caller of this
         snippet passes money, and the label is what the money MEANS. -->
    <div
      class="redact truncate font-display text-[1.2rem] font-bold leading-none tabular-nums tracking-tight
             2xl:text-[1.35rem] {segment ? 'transition-opacity group-hover/metric:opacity-70' : ''}"
      style:color={color}
    >{value}</div>
    <div
      class="eyebrow mt-1.5 truncate text-[0.5rem] text-base-content/55
             {segment ? 'group-hover/metric:text-primary' : ''}"
    >{label}</div>
  </svelte:element>
{/snippet}

<!-- ONE figure on the dense strip: label and value on a line, not stacked. Four of
     these fit a row that the full tiles need 220px of vertical space to say. -->
{#snippet denseFigure(label: string, value: string, exact: string,
                      color: string | null, segment: string)}
  <button
    type="button"
    onclick={() => openSegment(segment)}
    class="group/d flex min-w-0 items-baseline gap-1.5"
    title={`${exact} — open the ${SEGMENT_BOARD[segment] === 'engagement' ? 'engagement' : 'membership'} board`}
  >
    <span class="eyebrow shrink-0 text-[0.5rem] text-base-content/50 group-hover/d:text-primary">{label}</span>
    <!-- Redacted like its full-size twin: the same money, at a smaller size, is still
         this community's money. -->
    <span
      class="redact truncate font-display text-sm font-bold tabular-nums leading-none tracking-tight transition-opacity group-hover/d:opacity-70"
      style:color={color}
    >{value}</span>
  </button>
{/snippet}

{#snippet openLink(text: string, seg: string | null = null)}
  <button
    type="button"
    onclick={() => (seg ? openSegment(seg) : openBoard())}
    class="text-xs font-medium text-primary transition-colors hover:text-primary/80"
  >{text} →</button>
{/snippet}

{#snippet linkTo(text: string, href: string)}
  <button
    type="button"
    onclick={() => navigate(href)}
    class="text-xs font-medium text-primary transition-colors hover:text-primary/80"
  >{text} →</button>
{/snippet}

{#if dense}
  <!-- THE STRIP. No section eyebrow, no mix bars, no legends, no per-tile footer links:
       on the Pipelines page the boards those links point at are already on screen, and
       the bars answer a question ("made up of what") that page is not asking. What is
       left is the four figures worth glancing at and the click through to each one's
       members. -->
  {#if blocked}
    <!-- The ask still has to be makeable here — it is the reason the numbers are absent
         — but as one line rather than a panel. -->
    <button
      type="button"
      onclick={openBoard}
      class="mb-4 flex w-full items-center gap-2 rounded-box border border-warning/35 bg-warning/[0.07] px-4 py-2 text-left text-xs text-base-content/75 transition-colors hover:border-warning/55"
    >
      <span class="eyebrow shrink-0 text-[0.5rem] text-warning">Revenue</span>
      <span class="min-w-0 flex-1 truncate">
        {rosterState === 'escalate'
          ? 'We’re in, but Skool won’t show us billing — an admin role usually opens it.'
          : 'Add our account as a moderator to see plans, prices and renewals.'}
      </span>
      <span class="shrink-0 font-medium text-primary">Open the membership board →</span>
    </button>
  {:else if r}
    <div class="hair mb-4 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-box border bg-base-100 px-4 py-2.5">
      <!-- One line each, like their full-size twins above. Wrapping the arguments put the
           money expression on a line of its own, which the privacy checker reads as an
           unmarked render — it cannot tell an argument from an interpolation. The value
           these produce is a `title`, which private mode strips at runtime anyway. -->
      {@render denseFigure('MRR', compact(r.mrr_cents), `${money(r.mrr_cents)} per month`, POSITIVE, 'monthly')}
      {@render denseFigure('Leaving', signedCompact(r.cancelling_mrr_cents), `${money(r.cancelling_mrr_cents)} per month — cancelled and declined`, r.cancelling_mrr_cents > 0 ? DANGER : null, 'cancelled')}
      {@render denseFigure('At risk', signedCompact(r.quiet_mrr_cents), `${money(r.quiet_mrr_cents)} per month from paying members who are not contributing`, r.quiet_mrr_cents > 0 ? DANGER : null, 'risk_to_cancel')}
      {@render denseFigure('Churned', r.churned_members.toLocaleString(), `${r.churned_members} paying members lost, all time`, null, 'churned')}
      {#if updated}
        <span
          class="ml-auto shrink-0 font-mono text-[0.65rem] text-base-content/45"
          title={`Membership last read ${parseTs(updated).toLocaleString()}`}
        >as of {timeAgo(updated, nowMs)}</span>
      {/if}
    </div>
  {/if}
{:else}
<section class="mb-6">
  <div class="mb-2 flex items-baseline justify-between gap-3">
    <!-- "Revenue", not "Recurring revenue": that is the first TILE's name, and a section
         label that restates its own first child is the duplication `<PageHeader>` exists
         to make unrepresentable. Same rule, one level down. -->
    <div class="eyebrow">Revenue</div>
    {#if updated && !blocked}
      <span
        class="font-mono text-[0.7rem] text-base-content/55"
        title={`Membership last read ${parseTs(updated).toLocaleString()}`}
      >as of {timeAgo(updated, nowMs)}</span>
    {/if}
  </div>

  {#if blocked}
    <!-- One panel rather than three empty tiles: there is nothing to compare, and three
         zeros would state a falsehood about the community's revenue. -->
    <div class="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 rounded-box border border-warning/35 bg-warning/[0.07] px-5 py-4">
      <div class="min-w-0">
        <div class="font-display text-base font-semibold">
          {rosterState === 'escalate'
            ? 'We’re in, but Skool won’t show us billing'
            : 'Add us as a moderator to see revenue'}
        </div>
        <p class="mt-1 max-w-2xl text-sm text-base-content/72">
          {#if rosterState === 'escalate'}
            Plans, prices and renewal dates live on the members page, and the role we hold
            here doesn’t open it — an admin role usually does.
          {:else}
            Plans, prices, cancellations and renewal dates come from your members page,
            which only staff can open. Add our account as a moderator and these fill in on
            the next sweep.
          {/if}
        </p>
      </div>
      <div class="shrink-0">{@render openLink('Open the membership board')}</div>
    </div>
  {:else if r}
    <div class="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
      <!-- HAND-BUILT rather than a `<StatCard>`, and this is the one place that is
           right: the tile carries THREE co-equal headline figures. MRR, ARR and
           collected-to-date are separate metrics over separate populations, so demoting
           any of them to a sub-line would make it look derived from the others — which
           is the exact misreading this widget was rebuilt to remove. The box, the type
           scale and the info tip are StatCard's, so it still reads as one of the set. -->
      <div class="relative rounded-box border hair bg-base-100 px-4 py-3">
        <!-- privacy-ok: explains what MRR/ARR MEAN — product copy, not this community's figures -->
        <InfoTip
          class="absolute right-2 top-2"
          text={'MRR counts monthly plans only; ARR counts annual plans only. They are two metrics over two groups of members and neither is derived from the other — an annual plan is never divided by 12, because someone who pays a year up front and cancels the next day never paid a monthly amount. Collected is every payment this community has ever taken, all-time, including from members who have since left. Members on a tier with no published price are real payers who add to neither rate, which is why both are floors and why the mix bar shows them.'}
        />
        <div class="eyebrow mb-1.5 pr-4">Recurring</div>

        {#if noPrices}
          <div class="font-display text-[1.2rem] font-bold leading-none tabular-nums tracking-tight text-base-content/70 2xl:text-[1.35rem]">—</div>
          <div class="mt-1 font-mono text-[0.7rem] leading-tight text-base-content/65">
            {r.paying.toLocaleString()} paying {plural(r.paying, 'member', 'members')} · no prices published yet
          </div>
        {:else}
          <div class="grid grid-cols-3 gap-x-3">
            {@render metric(compact(r.mrr_cents), 'MRR', `${money(r.mrr_cents)} per month`, POSITIVE, 'monthly')}
            {@render metric(compact(r.arr_cents), 'ARR', `${money(r.arr_cents)} per year`, POSITIVE, 'annual')}
            {@render metric(compact(r.lifetime_cents), 'Collected', `${money(r.lifetime_cents)} collected all time`, POSITIVE, 'collected')}
          </div>

          <div class="mt-3">
            <MixBar
              label="Plan mix"
              footnote={`${r.paying} paying · ${r.priced} with a published price`}
              segments={[
                { key: 'monthly', label: 'monthly', n: r.monthly_members, color: '#4a6aa8', amount: `${money(r.mrr_cents)}/mo`, onSelect: () => openSegment('monthly') },
                { key: 'annual', label: 'annual', n: r.annual_members, color: '#2f4373', amount: `${money(r.arr_cents)}/yr`, onSelect: () => openSegment('annual') },
                { key: 'unpriced', label: 'no price', n: Math.max(0, r.paying - r.priced), color: '#9aa3b2', amount: null, onSelect: () => openSegment('unpriced') },
              ]}
            />
          </div>
        {/if}

        <div class="mt-2.5">{@render openLink('Open the membership board', 'paying')}</div>
      </div>

      <div class="relative rounded-box border hair bg-base-100 px-4 py-3">
        <!-- privacy-ok: explains what MRR/ARR MEAN — product copy, not this community's figures -->
        <InfoTip
          class="absolute right-2 top-2"
          text={'Leaving MRR and ARR count everyone on their way out of the paying relationship — members who cancelled, and members whose card was declined. Both are still paying until their period ends, and both sit in the board\'s Cancelled column, which is why one figure covers them; the bar splits the two, because a decline is usually recoverable with a new card where a cancellation is a decision. Risk to cancel is a separate figure and is never added to it: paying members who are not contributing right now, which on most communities is the largest group, and none of whom have actually cancelled. Dropping off — someone who was engaged and whose activity then fell away — sits in the bar rather than the headline, because it is the weakest signal of the three. The bar covers every paying member: cancelled, dropping off, risk to cancel and engaged do not overlap and add up to the whole paying population.'}
        />
        <div class="eyebrow mb-1.5 pr-4">At risk</div>
        <div class="grid grid-cols-3 gap-x-3">
          <!-- CANCELLING ONLY. This used to add the slipping members' money in, which
               made one figure out of a fact (they cancelled, here is the date) and a
               guess (they went quiet, they might leave) — actionable as neither. -->
          {@render metric(signedCompact(r.cancelling_mrr_cents), 'Leaving MRR', `${money(r.cancelling_mrr_cents)} per month — cancelled and declined`, r.cancelling_mrr_cents > 0 ? DANGER : null, 'cancelled')}
          {@render metric(signedCompact(r.cancelling_arr_cents), 'Leaving ARR', `${money(r.cancelling_arr_cents)} per year — cancelled and declined`, r.cancelling_arr_cents > 0 ? DANGER : null, 'cancelled')}
          {@render metric(signedCompact(r.quiet_mrr_cents), 'Risk to cancel', `${money(r.quiet_mrr_cents)} per month from paying members who are not contributing`, r.quiet_mrr_cents > 0 ? DANGER : null, 'risk_to_cancel')}
        </div>

        {#if r.paying > 0}
          <!-- A FULL PARTITION of the paying population: the healthy remainder is drawn
               too, so the bar fills. It used to stop short by however many payers were
               fine, and an under-filled bar with no explanation reads as missing data
               rather than as good news. -->
          <div class="mt-3">
            <MixBar
              label="Paying members"
              footnote={`${r.paying} paying`}
              segments={[
                { key: 'leaving', label: 'cancelled', n: r.leaving_members, color: '#bd3a30', amount: `${money(r.cancelling_mrr_cents)}/mo`, onSelect: () => openSegment('cancelled') },
                { key: 'slipping', label: 'dropping off', n: r.slipping_members, color: '#d98a41', amount: `${money(r.slipping_mrr_cents)}/mo`, onSelect: () => openSegment('dropping_off') },
                { key: 'quiet', label: 'risk to cancel', n: r.quiet_members, color: '#cf8b84', amount: `${money(r.quiet_mrr_cents)}/mo`, onSelect: () => openSegment('risk_to_cancel') },
                { key: 'engaged', label: 'engaged', n: r.engaged_members, color: '#157a49', amount: `${money(r.engaged_mrr_cents)}/mo`, onSelect: () => openSegment('engaged') },
              ]}
            />
          </div>
        {/if}

        <div class="mt-2.5">{@render openLink('Work the at-risk members', 'cancelled')}</div>
      </div>

      <div class="relative rounded-box border hair bg-base-100 px-4 py-3">
        <!-- privacy-ok: explains what MRR/ARR MEAN — product copy, not this community's figures -->
        <InfoTip
          class="absolute right-2 top-2"
          text={'Every paying membership this community has ever lost, as a snapshot rather than a rate. Already out of the MRR and ARR figures above, so it sits beside them rather than being subtracted from them — these are people who have stopped paying, not a deduction from what is still coming in.'}
        />
        <div class="eyebrow mb-1.5 pr-4">Churned · all time</div>
        <div class="grid grid-cols-3 gap-x-3">
          {@render metric(signedCompact(r.churned_mrr_cents), 'Lost MRR', `${money(r.churned_mrr_cents)} per month`, r.churned_mrr_cents > 0 ? DANGER : null, 'churned')}
          {@render metric(signedCompact(r.churned_arr_cents), 'Lost ARR', `${money(r.churned_arr_cents)} per year`, r.churned_arr_cents > 0 ? DANGER : null, 'churned')}
          {@render metric(r.churned_members.toLocaleString(), 'Members', `${r.churned_members} paying members lost, all time`, null, 'churned')}
        </div>

        {#if r.churned_members > 0}
          <div class="mt-3">
            <MixBar
              label="What they were on"
              footnote={`${r.churned_members} lost, all time`}
              segments={[
                { key: 'monthly', label: 'monthly', n: r.churned_monthly_members, color: '#4a6aa8', amount: `${signed(r.churned_mrr_cents)}/mo`, onSelect: () => openSegment('churned') },
                { key: 'annual', label: 'annual', n: r.churned_annual_members, color: '#2f4373', amount: `${signed(r.churned_arr_cents)}/yr`, onSelect: () => openSegment('churned') },
                { key: 'unpriced', label: 'no price', n: r.churned_unpriced_members, color: '#9aa3b2', amount: null, onSelect: () => openSegment('churned') },
              ]}
            />
          </div>
        {:else}
          <div class="mt-3 font-mono text-[0.7rem] text-base-content/65">no paying member has ever left</div>
        {/if}

        <div class="mt-2.5">{@render linkTo('See who left', DEPARTED_MEMBERS)}</div>
      </div>

    </div>
  {/if}
</section>
{/if}
